import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Sparkles } from "lucide-react";
import Navigation from "@/components/landing/Navigation";
import FooterCTA from "@/components/landing/FooterCTA";
import FloatingBookCall from "@/components/landing/FloatingBookCall";
import ImageLightbox from "@/components/landing/ImageLightbox";
import { caseStudies, caseStudyOrder } from "@/data/caseStudies";

const accentMap = {
  emerald: { dot: "bg-emerald-500", text: "text-emerald-600", soft: "bg-emerald-50", chip: "border-emerald-200 text-emerald-700" },
  lime: { dot: "bg-lime-500", text: "text-lime-700", soft: "bg-lime-50", chip: "border-lime-200 text-lime-700" },
  yellow: { dot: "bg-yellow-500", text: "text-yellow-700", soft: "bg-yellow-50", chip: "border-yellow-200 text-yellow-800" },
  blue: { dot: "bg-brand-500", text: "text-brand-600", soft: "bg-brand-50", chip: "border-brand-200 text-brand-700", gradient: true },
};

export default function CaseStudy() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const study = caseStudies[slug];

  // Lightbox state: { images: string[], index: number } | null
  const [lightbox, setLightbox] = useState(null);
  const openLightbox = (images, i) => setLightbox({ images, index: i });
  const closeLightbox = () => setLightbox(null);
  const prevLightbox = () =>
    setLightbox((l) =>
      l ? { ...l, index: (l.index - 1 + l.images.length) % l.images.length } : l
    );
  const nextLightbox = () =>
    setLightbox((l) =>
      l ? { ...l, index: (l.index + 1) % l.images.length } : l
    );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!study) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center bg-white px-6 text-center">
        <p className="text-sm uppercase tracking-widest text-zinc-500">404</p>
        <h1 className="font-display text-4xl font-semibold tracking-tight mt-2">
          Case study not found
        </h1>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 bg-zinc-950 text-white text-sm px-5 py-2.5 rounded-full hover:bg-brand-600 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Back home
        </Link>
      </main>
    );
  }

  const a = accentMap[study.accent] || accentMap.emerald;
  const i = caseStudyOrder.indexOf(slug);
  const next = caseStudies[caseStudyOrder[(i + 1) % caseStudyOrder.length]];
  const isAiJobFinder = slug === "ai-job-finder";

  return (
    <main data-testid="case-study-page" className="relative w-full overflow-x-hidden bg-white">
      <Navigation />

      {/* Hero */}
      <section
        data-testid="case-study-hero"
        className={`pt-32 sm:pt-40 pb-12 sm:pb-16 ${a.soft}`}
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <button
            onClick={() => navigate(-1)}
            data-testid="case-study-back-btn"
            className="inline-flex items-center gap-2 text-sm text-zinc-700 hover:text-zinc-950 bg-white border border-zinc-200 hover:border-zinc-900 px-4 py-2 rounded-full transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> All projects
          </button>

          <div className="mt-8 sm:mt-12 grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-600">
                <span className={`h-1.5 w-1.5 rounded-full ${a.dot}`}></span>
                Case Study · {study.year}
                {study.comingSoon && (
                  <span className="ml-2 inline-flex items-center gap-1.5 brand-gradient text-white px-2.5 py-1 rounded-full text-[10px] font-semibold tracking-wider">
                    Coming soon
                  </span>
                )}
              </span>
              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                data-testid="case-study-title"
                className="mt-4 font-display text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tighter text-zinc-950 leading-[1.05]"
              >
                {study.title}
              </motion.h1>
              <p className="mt-4 text-xl sm:text-2xl text-zinc-700 font-light max-w-2xl">
                {study.tagline}
              </p>
            </div>

            <div className="lg:col-span-4 grid grid-cols-2 gap-4 text-sm">
              <Meta label="Role" value={study.role} />
              <Meta label="Timeline" value={study.timeline} />
              <Meta label="Team" value={study.team} />
              <Meta label="Platform" value={study.platform} />
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-12 sm:mt-16"
          >
            <img
              src={study.heroImage}
              alt={study.title}
              className="w-full h-auto rounded-3xl ring-1 ring-black/5"
            />
          </motion.div>
        </div>
      </section>

      {/* Overview */}
      {!study.comingSoon && (
        <section data-testid="case-study-overview" className="py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <span className="text-xs uppercase tracking-widest text-zinc-500">
              Overview
            </span>
          </div>
          <div className="lg:col-span-8">
            <p className="font-display text-2xl sm:text-3xl text-zinc-900 leading-snug tracking-tight font-light">
              {study.overview}
            </p>
            {study.problem && (
              <div className="mt-10 border-l-2 border-zinc-200 pl-6">
                <span className="text-xs uppercase tracking-widest text-zinc-500">
                  The Problem
                </span>
                <p className="mt-3 text-zinc-700 text-lg leading-relaxed max-w-2xl">
                  {study.problem}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>
      )}

      {/* Coming soon placeholder */}
      {study.comingSoon && (
        <section
          data-testid="case-study-coming-soon"
          className="py-24 sm:py-32 bg-white"
        >
          <div className="max-w-3xl mx-auto px-6 sm:px-8 text-center">
            <span className="inline-flex items-center gap-2 brand-gradient text-white px-4 py-2 rounded-full text-xs font-semibold tracking-widest uppercase">
              Coming soon
            </span>
            <h2 className="mt-6 font-display text-4xl sm:text-5xl font-semibold tracking-tighter text-zinc-950">
              This case study is in the works.
            </h2>
            <p className="mt-5 text-zinc-600 text-lg leading-relaxed">
              A full write-up — process, decisions, and outcomes — is on the
              way. Want a preview or to chat about the project in the meantime?
            </p>
            <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
              <Link
                to="/#contact"
                data-testid="coming-soon-contact-btn"
                className="inline-flex items-center gap-2 bg-zinc-950 text-white hover:bg-brand-600 transition-colors text-sm font-medium px-6 py-3 rounded-full"
              >
                Get in touch
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-2 bg-white text-zinc-900 border border-zinc-200 hover:border-zinc-900 transition-colors text-sm font-medium px-6 py-3 rounded-full"
              >
                <ArrowLeft className="h-4 w-4" /> Back to projects
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Process */}
      {!study.comingSoon && study.process && (
        <section
          data-testid="case-study-process"
          className="py-20 sm:py-28 bg-zinc-50 border-y border-zinc-200/60"
        >
          <div className="max-w-7xl mx-auto px-6 sm:px-8 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <span className="text-xs uppercase tracking-widest text-zinc-500">
                01 · Approach
              </span>
              <h2 className="mt-3 font-display text-4xl sm:text-5xl font-semibold tracking-tighter text-zinc-950">
                {study.process.heading}
              </h2>
            </div>
            <div className="lg:col-span-8">
              <p className="text-zinc-700 text-lg leading-relaxed max-w-2xl">
                {study.process.body}
              </p>
              <div className="mt-10 grid sm:grid-cols-2 gap-5">
                {study.process.steps.map((s, idx) => (
                  <div
                    key={s.label}
                    data-testid={`process-step-${idx}`}
                    className="bg-white rounded-2xl p-6 ring-1 ring-zinc-200/60"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-xs font-mono text-zinc-500">
                        ({String(idx + 1).padStart(2, "0")})
                      </span>
                      <span className="font-display text-lg font-medium tracking-tight">
                        {s.label}
                      </span>
                    </div>
                    <p className="text-sm text-zinc-600 leading-relaxed">
                      {s.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Features */}
      {!study.comingSoon && study.features?.length > 0 && (
        <section data-testid="case-study-features" className="py-20 sm:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            <div className="grid lg:grid-cols-12 gap-10 mb-12 sm:mb-16">
              <div className="lg:col-span-4">
                <span className="text-xs uppercase tracking-widest text-zinc-500">
                  02 · Features
                </span>
                <h2 className="mt-3 font-display text-4xl sm:text-5xl font-semibold tracking-tighter text-zinc-950">
                  What we shipped
                </h2>
              </div>
              <div className="lg:col-span-8">
                <p className="text-zinc-600 text-lg leading-relaxed max-w-2xl">
                  Each feature was driven by a specific insight from research,
                  shipped behind a metric, and iterated weekly.
                </p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              {study.features.map((f, idx) => (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: idx * 0.05 }}
                  data-testid={`feature-${idx}`}
                  className="group bg-zinc-50 hover:bg-white hover:ring-zinc-300 ring-1 ring-transparent rounded-2xl p-7 transition-all"
                >
                  <span
                    className={`inline-flex h-9 w-9 items-center justify-center rounded-full ${a.soft} ${a.text}`}
                  >
                    <Sparkles className="h-4 w-4" />
                  </span>
                  <h3 className="mt-5 font-display text-xl font-medium tracking-tight text-zinc-950">
                    {f.title}
                  </h3>
                  <p className="mt-2 text-zinc-600 text-sm leading-relaxed">
                    {f.body}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Customer Journey — one section per step */}
      {!study.comingSoon && study.journey?.length > 0 && (
        <section
          data-testid="case-study-journey"
          className="py-20 sm:py-28 bg-zinc-950 text-white"
        >
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            <div className="grid lg:grid-cols-12 gap-10 mb-12 sm:mb-20">
              <div className="lg:col-span-4">
                <span className="text-xs uppercase tracking-widest text-zinc-400">
                  03 · Customer journey
                </span>
                <h2 className="mt-3 font-display text-4xl sm:text-5xl font-semibold tracking-tighter">
                  Walk the user&apos;s path
                </h2>
              </div>
              <div className="lg:col-span-8">
                <p className="text-zinc-400 text-lg leading-relaxed max-w-2xl">
                  Each screen below maps to a real moment in the user&apos;s
                  journey — from the first 90 seconds to a steady-state where
                  the agent runs unattended.
                </p>
              </div>
            </div>

            <div className="space-y-24 sm:space-y-32">
              {study.journey.map((j, idx) => {
                return (
                  <motion.div
                    key={j.step}
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    data-testid={`journey-step-${idx}`}
                  >
                    {/* Copy on top */}
                    <div>
                      <div className="flex items-center gap-3 mb-5">
                        <span className="font-mono text-xs text-zinc-500">
                          ({j.step})
                        </span>
                        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-300 border border-white/15 px-3 py-1 rounded-full">
                          <span className={`h-1.5 w-1.5 rounded-full ${a.dot}`}></span>
                          {j.label}
                        </span>
                      </div>
                      <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-tight">
                        {j.title}
                      </h3>
                      <p className="mt-5 text-zinc-400 text-base sm:text-lg leading-relaxed">
                        {j.body}
                      </p>
                      {j.bullets?.length > 0 && (
                        <ul className="mt-6 space-y-2.5">
                          {j.bullets.map((b, bi) => (
                            <li
                              key={bi}
                              className="flex items-start gap-3 text-sm text-zinc-300"
                            >
                              <span className={`mt-1.5 h-1.5 w-1.5 rounded-full ${a.dot} shrink-0`}></span>
                              <span>{b}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    {/* Mockups below, horizontal */}
                    <div className="mt-10">
                      {j.images?.length > 0 ? (
                        <div className="flex flex-wrap gap-3 sm:gap-4">
                          {j.images.map((img, ii) => (
                            <button
                              type="button"
                              key={ii}
                              onClick={() => openLightbox(j.images, ii)}
                              data-testid={`mockup-${idx}-${ii}`}
                              className={`${
                                isAiJobFinder || slug === "fontalk"
                                  ? "w-[220px] sm:w-[280px] lg:w-[340px]"
                                  : "w-[140px] sm:w-[170px]"
                              } rounded-2xl overflow-hidden bg-zinc-900/40 ring-1 ring-white/5 p-2 flex items-start justify-center hover:ring-white/30 hover:-translate-y-1 transition-all cursor-zoom-in`}
                            >
                              <img
                                src={img}
                                alt={`${j.title} – ${ii + 1}`}
                                loading="lazy"
                                className="block w-full h-auto rounded-lg pointer-events-none"
                              />
                            </button>
                          ))}
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => j.image && openLightbox([j.image], 0)}
                          data-testid={`mockup-single-${idx}`}
                          className="block rounded-3xl overflow-hidden bg-zinc-900/40 ring-1 ring-white/5 p-4 sm:p-8 max-w-4xl cursor-zoom-in hover:ring-white/30 transition-all"
                        >
                          <img
                            src={j.image}
                            alt={j.title}
                            loading="lazy"
                            className="w-full h-auto block pointer-events-none"
                          />
                        </button>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Screens */}
      {!study.comingSoon && study.screens?.length > 0 && (
        <section
          data-testid="case-study-screens"
          className="py-20 sm:py-28 bg-zinc-950 text-white"
        >
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            <div className="grid lg:grid-cols-12 gap-10 mb-12 sm:mb-16">
              <div className="lg:col-span-4">
                <span className="text-xs uppercase tracking-widest text-zinc-400">
                  03 · The product
                </span>
                <h2 className="mt-3 font-display text-4xl sm:text-5xl font-semibold tracking-tighter">
                  In the hands of users
                </h2>
              </div>
              <div className="lg:col-span-8">
                <p className="text-zinc-400 text-lg leading-relaxed max-w-2xl">
                  Select screens from the live product. Each one is the result
                  of multiple research → design → measure cycles.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {study.screens.map((s, idx) => (
                <motion.figure
                  key={idx}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: idx * 0.05 }}
                  data-testid={`screen-${idx}`}
                  className="group"
                >
                  <div className="aspect-[9/16] rounded-2xl overflow-hidden bg-zinc-900/50 ring-1 ring-white/5 p-4 transition-transform duration-500 group-hover:-translate-y-1 flex items-center justify-center">
                    <img
                      src={s.src}
                      alt={s.caption}
                      loading="lazy"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <figcaption className="mt-3 text-xs text-zinc-400 px-1">
                    {s.caption}
                  </figcaption>
                </motion.figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Outcomes */}
      {!study.comingSoon && study.outcomes?.length > 0 && (
        <section data-testid="case-study-outcomes" className="py-20 sm:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-6 sm:px-8">
            <div className="grid lg:grid-cols-12 gap-10 mb-12 sm:mb-16">
              <div className="lg:col-span-4">
                <span className="text-xs uppercase tracking-widest text-zinc-500">
                  04 · Impact
                </span>
                <h2 className="mt-3 font-display text-4xl sm:text-5xl font-semibold tracking-tighter text-zinc-950">
                  What changed
                </h2>
              </div>
              <div className="lg:col-span-8">
                <p className="text-zinc-600 text-lg leading-relaxed max-w-2xl">
                  Measured numbers, taken from production analytics after the
                  redesign shipped.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {study.outcomes.map((o, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.06 }}
                  data-testid={`outcome-${idx}`}
                  className={`rounded-2xl p-6 sm:p-8 ${a.soft}`}
                >
                  <p
                    className={`font-display text-4xl sm:text-5xl font-semibold tracking-tighter ${
                      a.gradient ? "brand-gradient-text-dark" : a.text
                    }`}
                  >
                    {o.metric}
                  </p>
                  <p className="mt-3 text-sm text-zinc-700 leading-snug">
                    {o.label}
                  </p>
                </motion.div>
              ))}
            </div>

            {/* Fullscreen product video — above the live CTA */}
            {study.video && (
              <div
                data-testid="case-study-video"
                className="mt-12 -mx-6 sm:-mx-8 lg:mx-0 lg:rounded-3xl overflow-hidden bg-black"
              >
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                  className="block w-full h-auto"
                >
                  <source src={study.video} type="video/mp4" />
                  <source src={study.video} type="video/quicktime" />
                </video>
              </div>
            )}

            {study.liveUrl && (
              <div className="mt-12 flex justify-center">
                <a
                  href={study.liveUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="case-study-live-btn"
                  className="inline-flex items-center gap-2 bg-zinc-950 text-white hover:bg-brand-600 transition-colors text-sm font-medium px-6 py-3 rounded-full"
                >
                  Visit the live product
                  <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Next case study */}
      <section className="py-20 sm:py-28 bg-zinc-50 border-t border-zinc-200/60">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 flex items-center justify-between flex-wrap gap-6">
          <div>
            <span className="text-xs uppercase tracking-widest text-zinc-500">
              Next case study
            </span>
            <p className="mt-2 font-display text-3xl sm:text-4xl font-semibold tracking-tighter text-zinc-950">
              {next.title}
            </p>
          </div>
          <Link
            to={`/case-studies/${next.slug}`}
            data-testid="next-case-study-btn"
            className="inline-flex items-center gap-2 bg-zinc-950 text-white hover:bg-brand-600 transition-colors text-sm font-medium px-6 py-3 rounded-full"
          >
            Read next
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <FooterCTA />
      <FloatingBookCall />
      <ImageLightbox
        images={lightbox?.images || []}
        index={lightbox?.index ?? null}
        onClose={closeLightbox}
        onPrev={prevLightbox}
        onNext={nextLightbox}
      />
    </main>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-widest text-zinc-500">{label}</p>
      <p className="mt-1 text-zinc-900 font-medium">{value}</p>
    </div>
  );
}
